import { VerifiedBadge } from 'iconoir-react';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { FAIL, LOADING, SUCCESS } from '../../../redux_store/actionTypes';
import { getAllPaymentMenthods } from '../../../redux_store/payment_methods/payment_methods_thunk';
import { clearPromoState } from '../../../redux_store/promotions/promotion_slice';
import { addPromo, applyPromotion } from '../../../redux_store/promotions/promotion_thunk';
import {
  creditRefund,
  getUserById,
  updateAccountStatusById,
  updateUserDetailsById,
  updateUserVerificationStatus
} from '../../../redux_store/users/users_thunk';
import { getAllVehicles } from '../../../redux_store/vehicles/vehicle_thunk';
import { displayFile } from '../../../utils';
import Button from '../../templates/Button';
import InputField from '../../templates/InputField';
import toast from '../../templates/ToastMessage';
import BasicDropDown from '../../templates/dropdowns/BasicDropDown';
import ApplyPromoModal from '../../templates/modals/ApplyPromoModal';
import ImageFullScreen from '../../templates/modals/ImageFullScreen';
import LoadingModal from '../../templates/modals/LoadingModal';
import RefundModal from '../../templates/modals/RefundModal';
import VerificationModal from '../../templates/modals/VerificationModal';

const ManageUsers = () => {
  const { users: user, message, status } = useSelector((state) => state.user);
  const { payment_methods } = useSelector((state) => state.payment_method);
  const promo = useSelector((state) => state.promotion);
  const { vehicles } = useSelector((state) => state.vehicle);
  const [editMode, setEditMode] = useState(false);
  const [imgDimensions, setImgDimensions] = useState('h-1/4');
  const dispatch = useDispatch();
  const navigator = useNavigate();
  const [imgFullScreen, setImgFullScreen] = useState({ show: false, img: '' });
  const [basicInfo, setBasicInfo] = useState({});
  const [accountSettingsInfo, setAccountSettingsInfo] = useState({});
  const [driverOtherInfo, setDriverOtherInfo] = useState({});
  const profilePicRef = useRef(null);
  const idCardRef = useRef(null);
  const newProfilePicRef = useRef(null);
  const newDriverLicPicRef = useRef(null);
  const newCardPicRef = useRef(null);
  const [showModal, setShowModal] = useState({
    refund: false, review: false, applyPromo: false
  })
  const driverLicenseRef = useRef(null);
  const params = useParams();

  useEffect(() => {
    if (status === FAIL) {
      toast({ message, status });
    } else if (promo.status === FAIL) {
      toast({ message: promo.message, status: promo.status });
      dispatch(clearPromoState())
    } else if (status === SUCCESS) {
      setShowModal({ ...showModal, review: false });
      toast({ message, status });
      if (editMode) {
        dispatch(getUserById(params.id));
        // navigator('/users');
      } else {
        dispatch(getUserById(params.id));
      }
    } else if (promo.status === SUCCESS) {
      toast({ message: promo.message, status: promo.status });
      setShowModal({ ...showModal, refund: false, applyPromo: false });
    }
    // eslint-disable-next-line
  }, [status, promo.status]);

  useEffect(() => {
    if (params.id && params.operation) {
      if (params.operation === 'edit') {
        setEditMode(true);
      }
      dispatch(getAllVehicles())
      dispatch(getAllPaymentMenthods());
      dispatch(getUserById(params.id));
    }
    // eslint-disable-next-line
  }, [params.id]);

  const handleInputChange = (e, type) => {
    const { name, value } = e.target;
    if (type === 'account') {
      setAccountSettingsInfo({ ...accountSettingsInfo, [name]: value })
    } else if (type === 'driver') {
      setDriverOtherInfo({ ...driverOtherInfo, [name]: value })
    } else {
      setBasicInfo({ ...basicInfo, [name]: value });
    }
  }

  const handleImageSelect = (imageRef, imageVal, callback) => {
    const image = imageRef.current;
    image.click();
    image.addEventListener('change', (_) => {
      console.log(image.files[0]);
      callback(image.files[0]);
      displayFile(image.files[0], imageVal.current);
    })
  }

  const handleFormSubmit = (e) => {
    e.preventDefault();
    dispatch(updateUserDetailsById({
      basicInfo,
      driverOtherInfo,
      accountSettingsInfo,
      id: params.id
    }));
  }

  useEffect(() => {
    if (!Array.isArray(user)) {
      displayFile(user.profile_pic, profilePicRef.current);
      displayFile(user.id_card_img, idCardRef.current);
      setBasicInfo(user);
      setAccountSettingsInfo(user.user_account_setting ?? {});
      if (user.role === 'driver') {
        displayFile(user.driver_profile.driving_license_pic, driverLicenseRef.current);
        setImgDimensions('h-1/4');
        setDriverOtherInfo(user.driver_profile);
      } else {
        setImgDimensions('h-1/3')
      }
    }
  }, [user]);

  return (
    <div className="grid grid-cols-6 bg-white gap-10 p-5 rounded-md shadow-md">
      {showModal.refund &&
        <RefundModal
          user={user}
          onCancel={() => setShowModal({ ...showModal, refund: false })}
          onSubmit={(details, { credit, promo }) => {
            if (credit) {
              dispatch(creditRefund({ user_id: user.id, amount: details.amount }))
            } else {
              dispatch(addPromo(details))
            }
          }}
        />
      }
      {showModal.applyPromo &&
        <ApplyPromoModal
          onSubmit={({ promo_code, user_id }) => {
            dispatch(applyPromotion({ promo_code, user_id }));
          }}
          onCancel={() => setShowModal({ ...showModal, applyPromo: false })}
          user={user}
        />
      }
      {showModal.review &&
        <VerificationModal
          onCancel={() => setShowModal({ ...showModal, review: false })}
          onSubmit={({ status, verification_comment }) => {
            dispatch(updateUserVerificationStatus({
              id: params.id, status: status.label, verification_comment
            }))
          }}
          currentComment={accountSettingsInfo.verification_comment}
          currentStatus={accountSettingsInfo.verification_status}
        />
      }
      <input type={'file'} ref={newProfilePicRef} hidden />
      <input type={'file'} ref={newDriverLicPicRef} hidden />
      <input type={'file'} ref={newCardPicRef} hidden />
      {imgFullScreen.show && <ImageFullScreen
        src={imgFullScreen.img}
        onClose={(e) => {
          if (!e.target.src) {
            setImgFullScreen({ show: false, img: '' });
          }
        }}
      />}
      {(status === LOADING || promo.status === LOADING) && <LoadingModal message={'Please wait...'} />}
      <div className='col-span-2 space-y-4'>
        <p className='text-gray-600 text-sm font-semibold'>Profile Picture</p>
        <div className={`${imgDimensions} relative `}>
          <div className='flex right-3 space-x-4 top-5 absolute'>
            {editMode && <small
              onClick={() => handleImageSelect(
                newProfilePicRef,
                profilePicRef,
                (image) => setBasicInfo({ ...basicInfo, profile_pic: image })
              )}
              className='cursor-pointer ml-2 bg-yellow-600 p-1 font-bold rounded-md text-gray-900 shadow-md'>
              Change Image
            </small>}
            <small
              onClick={() => {
                setImgFullScreen({ show: true, img: user.profile_pic });
              }}
              className='cursor-pointer ml-2 bg-white p-1 font-bold rounded-md text-gray-500 shadow-md'>
              Expand
            </small>
          </div>
          <div ref={profilePicRef} className={`h-full rounded-md shadow-md`}></div>
        </div>
        <p className='text-gray-600 text-sm font-semibold'>User ID Card ({basicInfo.id_card_type})</p>
        <div className={`${imgDimensions} relative `}>
          <div className='flex right-3 space-x-4 top-5 absolute'>
            {editMode && <small
              onClick={() => handleImageSelect(
                newCardPicRef,
                idCardRef,
                (image) => setBasicInfo({ ...basicInfo, id_card_img: image })
              )}
              className='cursor-pointer ml-2 bg-yellow-600 p-1 font-bold rounded-md text-gray-900 shadow-md'>
              Change Image
            </small>}
            <small
              onClick={() => {
                setImgFullScreen({ show: true, img: user.id_card_img });
              }}
              className='cursor-pointer ml-2 bg-white p-1 font-bold rounded-md text-gray-500 shadow-md'>
              Expand
            </small>
          </div>
          <div ref={idCardRef} className=' rounded-md shadow-md h-full'></div>
        </div>
        {user.role === 'driver' &&
          <>
            <p className='text-gray-600 text-sm font-semibold'>Driving License Picture</p>
          <div className={`${imgDimensions} relative `}>
            <div className='flex right-3 space-x-4 top-5 absolute'>
              {editMode && <small
                onClick={() => handleImageSelect(
                  newDriverLicPicRef,
                  driverLicenseRef,
                  (image) => setDriverOtherInfo({ ...driverOtherInfo, driving_license_pic: image })
                )}
                className='cursor-pointer ml-2 bg-yellow-600 p-1 font-bold rounded-md text-gray-900 shadow-md'>
                Change Image
              </small>}
              <small
                onClick={() => {
                  setImgFullScreen({ show: true, img: user.driving_license_pic });
                }}
                className='cursor-pointer ml-2 bg-white p-1 font-bold rounded-md text-gray-500 shadow-md'>
                Expand
              </small>
            </div>
              <div ref={driverLicenseRef}
                className={`rounded-t-md ${!editMode && 'rounded-b-md'} shadow-md h-full`}></div>
              {/* {editMode &&
                <button type='button' onClick={handleImageSelect} className='w-full rounded-b-md z-10 bg-blue-200 py-1'>change image</button>
              } */}
            </div>
          </>
        }
      </div>
      <div className='col-span-4 grid grid-cols-4 gap-y-2'>
        <div className="px-4 py-2 border col-span-4 border-blue-300 sm:px-6">
          <h3 className="text-md leading-6 font-semibold text-blue-600">
            Actions
          </h3>
        </div>
        <div className='flex col-span-4 space-x-5 mb-5'>
          {accountSettingsInfo.is_active ?
            <button
              type='button'
              onClick={() => {
                dispatch(updateAccountStatusById({
                  id: basicInfo.id, is_active: false
                }))
              }}
              className='text-gray-100 text-xs py-2 px-4 rounded-md font-medium bg-red-700 border'>
              Block user
            </button> :
            <button
              type='button'
              onClick={() => {
                dispatch(updateAccountStatusById({
                  id: basicInfo.id, is_active: true
                }))
              }}
              className='text-gray-100 text-xs py-2 px-4 rounded-md font-medium bg-green-600'>
              Unblock User
            </button>
          }
          {basicInfo.role === 'driver' &&
            <>
              {driverOtherInfo.available_for_delivery ?
                <button
                  type='button'
                  onClick={() => {
                    dispatch(updateUserDetailsById({
                      basicInfo,
                      driverOtherInfo: { ...driverOtherInfo, available_for_delivery: false },
                      accountSettingsInfo,
                      id: params.id
                    }));
                  }}
                  className='text-gray-100 text-xs py-2 px-4 rounded-md font-medium bg-red-700 border'>
                  Not Available for Delivery
                </button> :
                <button
                  type='button'
                  onClick={() => {
                    dispatch(updateUserDetailsById({
                      basicInfo,
                      driverOtherInfo: { ...driverOtherInfo, available_for_delivery: true },
                      accountSettingsInfo,
                      id: params.id
                    }));
                  }}
                  className='text-gray-100 text-xs py-2 px-4 rounded-md font-medium bg-green-600'>
                  Available for delivery
                </button>
              }
            </>
          }
          <button onClick={() => {
            navigator(`/users/trip-history/${params.id}`);
          }} className='text-gray-100 wayrideBgColor text-xs py-2 px-4 rounded-md font-medium'>Get Trip History</button>
          <button onClick={() => setShowModal({ ...showModal, refund: true })} className='text-gray-100 text-xs py-2 px-4 rounded-md font-medium wayrideBgColor'>Refund</button>
          <button className='text-gray-100 text-xs py-2 px-4 rounded-md font-medium bg-red-700'>Force Logout</button>
          <button onClick={() => setShowModal({ ...showModal, review: true })} className="text-gray-100 text-xs py-2 px-4 rounded-md font-medium wayrideBgColor">Review user</button>
          {basicInfo.role === 'customer' &&
            <button onClick={() => setShowModal({ ...showModal, applyPromo: true })} className="text-gray-100 text-xs py-2 px-4 rounded-md font-medium wayrideBgColor">Apply Promo</button>
          }
        </div>
        <form onSubmit={handleFormSubmit} className="grid grid-cols-2 col-span-4">
          <div className="overflow-hidden sm:rounded-md col-span-4">
            <div className="px-4 py-2 border border-blue-300 sm:px-6">
              <h3 className="text-md leading-6 font-semibold text-blue-600">
                Basic Account Info
              </h3>
            </div>
            <div>
              <dl>
                <div className="sm:grid my-5 sm:grid-cols-4 sm:gap-4 sm:px-6">
                  <dt className="text-sm my-auto col-span-2 leading-5 font-medium text-gray-500">
                    Surname
                  </dt>
                  <dt className="text-sm my-auto col-span-2 leading-5 font-medium text-gray-500">
                    <InputField
                      type={'text'}
                      name="surname"
                      disable={!editMode}
                      size="small"
                      isRequired={true}
                      onChange={handleInputChange}
                      value={basicInfo.surname}
                      placeholder="Enter Surname"
                    />
                  </dt>
                </div>
                <div className="sm:grid my-5 sm:grid-cols-4 sm:gap-4 sm:px-6">
                  <dt className="text-sm my-auto col-span-2 leading-5 font-medium text-gray-500">
                    Other names
                  </dt>
                  <dt className="text-sm my-auto col-span-2 leading-5 font-medium text-gray-500">
                    <InputField
                      type={'text'}
                      name="other_names"
                      isRequired={true}
                      disable={!editMode}
                      size="small"
                      onChange={handleInputChange}
                      value={basicInfo.other_names}
                      placeholder="Enter Othernames"
                    />
                  </dt>
                </div>
                <div className="sm:grid my-5 sm:grid-cols-4 sm:gap-4 sm:px-6">
                  <dt className="text-sm my-auto col-span-2 leading-5 font-medium text-gray-500">
                    Role
                  </dt>
                  <dt className="ml-4 text-sm my-auto col-span-2 leading-5 font-medium text-gray-500">
                    {basicInfo.role}
                  </dt>
                </div>
                <div className="bg-white sm:grid my-5 sm:grid-cols-4 sm:gap-4 sm:px-6">
                  <dt className="text-sm my-auto col-span-2 leading-5 font-medium text-gray-500">
                    Email Address
                  </dt>
                  <dt className="text-sm my-auto col-span-2 leading-5 font-medium text-gray-500">
                    <InputField
                      type={'email'}
                      name="email"
                      disable={!editMode}
                      size="small"
                      onChange={handleInputChange}
                      value={editMode ? basicInfo.email : basicInfo.email === null || basicInfo.email === '' ? 'N/A' : basicInfo.email}
                      placeholder="Enter Email"
                    />
                  </dt>
                </div>
                <div className="sm:grid my-5 sm:grid-cols-4 sm:gap-4 sm:px-6">
                  <dt className="text-sm my-auto col-span-2 leading-5 font-medium text-gray-500">
                    Phone Number
                  </dt>
                  <dt className="text-sm my-auto col-span-2 leading-5 font-medium text-gray-500">
                    <InputField
                      type={'text'}
                      name="phone_number"
                      disable={!editMode}
                      size="small"
                      isRequired={true}
                      onChange={handleInputChange}
                      value={basicInfo.phone_number}
                      placeholder="Enter Phone Number"
                    />
                  </dt>
                </div>
                <div className="sm:grid my-5 sm:grid-cols-4 sm:gap-4 sm:px-6">
                  <dt className="text-sm my-auto col-span-2 leading-5 font-medium text-gray-500">
                    Accepted Terms and Conditions
                  </dt>
                  <dt className="ml-4 text-sm my-auto col-span-2 leading-5 font-medium text-gray-500">
                    {basicInfo.has_accepted_terms ?
                      <p className='bg-green-700 inline-block text-xs rounded-md py-1 px-2 text-gray-100 animate-bounce'>Accepted</p> :
                      <p className='bg-red-700 inline-block text-xs rounded-md py-1 px-2 text-gray-100'>not accepted</p>
                    }
                  </dt>
                </div>
                <div className="sm:grid my-6 sm:grid-cols-4 sm:gap-4 sm:px-6">
                  <dt className="text-sm my-auto col-span-2 leading-5 font-medium text-gray-500">
                    Verification Status
                  </dt>
                  <dt className="ml-4 text-sm my-auto flex justify-between col-span-2 leading-5 font-medium text-gray-500">
                    <p className={`
                      ${accountSettingsInfo.verification_status === 'PENDING' ? 'text-yellow-700' :
                        accountSettingsInfo.verification_status === 'APPROVED' ? 'text-green-700' : 'text-red-700'} flex space-x-2`}>
                      {accountSettingsInfo.verification_status === 'APPROVED' && <VerifiedBadge />} <span>{accountSettingsInfo.verification_status}</span></p>
                  </dt>
                </div>
              </dl>
            </div>

            <div className="px-4 py-2 mt-10 border border-blue-300 sm:px-6">
              <h3 className="text-md leading-6 font-semibold text-blue-600">
                Account Settings Info
              </h3>
            </div>
            <div>
              <dl>
                <div className="sm:grid my-5 sm:grid-cols-4 sm:gap-4 sm:px-6">
                  <dt className="text-sm my-auto col-span-2 leading-5 font-medium text-gray-500">
                    Langauge
                  </dt>
                  <dt className="text-sm my-auto col-span-2 leading-5 font-medium text-gray-500">
                    <InputField
                      type={'text'}
                      name="language"
                      disable={!editMode}
                      size="small"
                      onChange={(e) => handleInputChange(e, 'account')}
                      value={accountSettingsInfo.language}
                      placeholder="Enter language"
                    />
                  </dt>
                </div>
                <div className="sm:grid my-5 sm:grid-cols-4 sm:gap-4 sm:px-6">
                  <dt className="text-sm my-auto col-span-2 leading-5 font-medium text-gray-500">
                    Is Online
                  </dt>
                  <dt className="ml-4 text-sm my-auto flex justify-between col-span-2 leading-5 font-medium text-gray-500">
                    {driverOtherInfo.is_active ?
                      <p className='bg-green-700 inline-block text-xs rounded-md py-1 px-2 text-gray-100 animate-bounce'>Online</p> :
                      <p className='bg-red-700 inline-block text-xs rounded-md py-1 px-2 text-gray-100'>Offline</p>
                    }
                  </dt>
                </div>
                <div className="sm:grid my-5 sm:grid-cols-4 sm:gap-4 sm:px-6">
                  <dt className="text-sm my-auto col-span-2 leading-5 font-medium text-gray-500">
                    Is Available For Ride
                  </dt>
                  <dt className="ml-4 text-sm my-auto flex justify-between col-span-2 leading-5 font-medium text-gray-500">
                    {driverOtherInfo.is_available ?
                      <p className='bg-green-700 inline-block text-xs rounded-md py-1 px-2 text-gray-100 animate-bounce'>Available</p> :
                      <p className='bg-red-700 inline-block text-xs rounded-md py-1 px-2 text-gray-100'>Not Available</p>
                    }
                  </dt>
                </div>
                <div className="sm:grid my-5 sm:grid-cols-4 sm:gap-4 sm:px-6">
                  <dt className="text-sm my-auto col-span-2 leading-5 font-medium text-gray-500">
                    Referral Code
                  </dt>
                  <dt className="ml-4 text-sm my-auto col-span-2 leading-5 font-medium text-gray-500">
                    {accountSettingsInfo.referral_code}
                  </dt>
                </div>
                <div className="sm:grid my-5 sm:grid-cols-4 sm:gap-4 sm:px-6">
                  <dt className="text-sm my-auto col-span-2 leading-5 font-medium text-gray-500">
                    Payment Method
                  </dt>
                  <dt className="text-sm my-auto col-span-2 leading-5 font-medium text-gray-500">
                    <BasicDropDown
                      onSelect={(e) => handleInputChange(e, 'account')}
                      name="payment_method"
                      value={accountSettingsInfo.payment_method}
                      disable={!editMode}
                      size="small"
                      items={payment_methods.map((method) => method.method)}
                    />
                  </dt>
                </div>
                <div className="bg-white sm:grid my-5 sm:grid-cols-4 sm:gap-4 sm:px-6">
                  <dt className="text-sm my-auto col-span-2 leading-5 font-medium text-gray-500">
                    Home Address
                  </dt>
                  <dt className="text-sm my-auto col-span-2 leading-5 font-medium text-gray-500">
                    <InputField
                      type={'text'}
                      name="home_address"
                      disable={!editMode}
                      size="small"
                      onChange={(e) => handleInputChange(e, 'account')}
                      value={
                        editMode ? accountSettingsInfo.home_address : accountSettingsInfo.home_address === null || accountSettingsInfo.home_address === '' ? 'N/A' : accountSettingsInfo.home_address
                      }
                      placeholder="Enter home address"
                    />
                  </dt>
                </div>
                <div className="sm:grid my-5 sm:grid-cols-4 sm:gap-4 sm:px-6">
                  <dt className="text-sm my-auto col-span-2 leading-5 font-medium text-gray-500">
                    Work Address
                  </dt>
                  <dt className="text-sm my-auto col-span-2 leading-5 font-medium text-gray-500">
                    <InputField
                      type={'text'}
                      name="work_address"
                      disable={!editMode}
                      size="small"
                      onChange={(e) => handleInputChange(e, 'account')}
                      value={
                        editMode ? accountSettingsInfo.work_address : accountSettingsInfo.work_address === null || accountSettingsInfo.work_address === '' ? 'N/A' : accountSettingsInfo.work_address
                      }
                      placeholder="Enter Work Address"
                    />
                  </dt>
                </div>
              </dl>
            </div>

            {basicInfo.role === 'driver' &&
              <>
                <div className="px-4 py-2 mt-10 border border-blue-300 sm:px-6">
                  <h3 className="text-md leading-6 font-semibold text-blue-600">
                    Driver Other Info
                  </h3>
                </div>

                <div>
                  <dl>
                    <div className="sm:grid my-5 sm:grid-cols-4 sm:gap-4 sm:px-6">
                      <dt className="text-sm my-auto col-span-2 leading-5 font-medium text-gray-500">
                        Available for delivery
                      </dt>
                      <dt className="ml-4 text-sm my-auto col-span-2 leading-5 font-medium text-gray-500">
                        {driverOtherInfo.available_for_delivery ? 'Yes' : 'No'}
                      </dt>
                    </div>
                    <div className="sm:grid my-5 sm:grid-cols-4 sm:gap-4 sm:px-6">
                      <dt className="text-sm my-auto col-span-2 leading-5 font-medium text-gray-500">
                        Vehicle Type
                      </dt>
                      <dt className="text-sm my-auto col-span-2 leading-5 font-medium text-gray-500">
                        <BasicDropDown
                          onSelect={(e) => handleInputChange(e, 'driver')}
                          name="vehicle_type"
                          value={driverOtherInfo.vehicle_type}
                          disable={!editMode}
                          size="small"
                          items={vehicles.map((vehicle) => vehicle.vehicle_types)}
                        />
                      </dt>
                    </div>
                    <div className="sm:grid my-5 sm:grid-cols-4 sm:gap-4 sm:px-6">
                      <dt className="text-sm my-auto col-span-2 leading-5 font-medium text-gray-500">
                        Vehicle Name
                      </dt>
                      <dt className="text-sm my-auto flex space-x-20 col-span-2 leading-5 font-medium text-gray-500">
                        <InputField
                          type={'text'}
                          name="vehicle_name"
                          disable={!editMode}
                          size="small"
                          onChange={(e) => handleInputChange(e, 'driver')}
                          value={driverOtherInfo.vehicle_name}
                          placeholder="Enter vehicle name"
                        />
                      </dt>
                    </div>
                    <div className="sm:grid my-5 sm:grid-cols-4 sm:gap-4 sm:px-6">
                      <dt className="text-sm my-auto col-span-2 leading-5 font-medium text-gray-500">
                        Vehicle Number Plate
                      </dt>
                      <dt className="text-sm my-auto col-span-2 leading-5 font-medium text-gray-500">
                        <InputField
                          type={'text'}
                          name="vehicle_number_plate"
                          disable={!editMode}
                          size="small"
                          onChange={(e) => handleInputChange(e, 'driver')}
                          value={
                            editMode ?
                              driverOtherInfo.vehicle_number_plate : driverOtherInfo.vehicle_number_plate === null ||
                                driverOtherInfo.vehicle_number_plate === '' ? 'N/A' : driverOtherInfo.vehicle_number_plate
                          }
                          placeholder="Enter vehicle plate number"
                        />
                      </dt>
                    </div>
                    <div className="sm:grid my-5 sm:grid-cols-4 sm:gap-4 sm:px-6">
                      <dt className="text-sm my-auto col-span-2 leading-5 font-medium text-gray-500">
                        Driving License Number
                      </dt>
                      <dt className="text-sm my-auto col-span-2 leading-5 font-medium text-gray-500">
                        <InputField
                          type={'text'}
                          name="driving_license_number"
                          disable={!editMode}
                          size="small"
                          onChange={(e) => handleInputChange(e, 'driver')}
                          value={
                            editMode ?
                              driverOtherInfo.driving_license_number : driverOtherInfo.driving_license_number === null ||
                                driverOtherInfo.driving_license_number === '' ? 'N/A' : driverOtherInfo.driving_license_number
                          }
                          placeholder="Enter driving license number"
                        />
                      </dt>
                    </div>
                  </dl>
                </div>
              </>
            }
            <div className='col-span-2'>
              {editMode &&
                <Button
                  type="submit"
                  label={'Update'}
                  width="w-full"
                  bgColor={'wayrideBgColor'}
                />
              }
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

export default ManageUsers